import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AgentHubItem, AgentCategory } from '../../models';
import { AgentHubService } from '../../services/agent-hub/agent-hub.service';
import { AgentOrchestratorService } from '../../services/agent-orchestrator/agent-orchestrator.service';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { StreamingStateService } from '../../../../shared/services/streaming-state/streaming-state.service';
import { TranslateService } from '@ngx-translate/core';

/**
 * Agent Hub Component
 * 
 * Main component for displaying all available agents in a filterable grid.
 * Integrates search, category filtering, and agent cards with favorites.
 * 
 * Features:
 * - Header with title and subtitle
 * - Search input with debounced filtering
 * - Category filter tabs (All, Regulations, Reports, Summarize)
 * - Responsive 2-column grid of agent cards
 * - Favorite toggling with persistence
 * - Loading and empty states
 * 
 * Usage:
 * <app-agent-hub></app-agent-hub>
 */
@Component({
  selector: 'app-agent-hub',
  templateUrl: './agent-hub.component.html',
  styleUrls: ['./agent-hub.component.scss']
})
export class AgentHubComponent implements OnInit, OnDestroy {

    /** Optional chatbot ref for child cards (e.g. RegRadar API on regulations agent click). */
  @Input() chatbotComponentRef: any = null;

  /** All available agents */
  allAgents: AgentHubItem[] = [];

  /** Filtered agents based on search and category */
  filteredAgents: AgentHubItem[] = [];

  /** Default agents (pinned in the Default section) */
  defaultAgents: AgentHubItem[] = [];

  /** Available filter categories */
  categories: AgentCategory[] = [];

  /** Currently selected category ID */
  activeCategory: string = 'all';

  /** Current search query */
  searchQuery: string = '';

  /** Set of favorite agent IDs for quick lookup */
  favoriteIds: Set<string> = new Set();

  /** Loading state */
  isLoading: boolean = true;

  /** Whether favorite filter is active */
  isFavoriteFilterActive: boolean = false;

  /** Whether agent info popup is visible */
  showInfoPopup: boolean = false;

  /** Currently selected agent for info popup */
  selectedAgentForInfo: AgentHubItem | null = null;

  /** Subject for cleanup on destroy */
  private readonly destroy$ = new Subject<void>();

  /** LocalStorage key for default agents */
  private readonly DEFAULT_AGENTS_KEY = 'freya_default_agents';

  constructor(
    private readonly agentHubService: AgentHubService,
    private readonly agentOrchestrator: AgentOrchestratorService,
    private readonly logger: LoggerService,
    private streamingStateService: StreamingStateService,
    private translate: TranslateService
  ) {}

  /**
   * Initialize component and load data
   * Categories are loaded dynamically after agents are fetched
   */
  ngOnInit(): void {
    this.loadAgents();
    this.subscribeFavorites();
  }

  /**
   * Clean up subscriptions on destroy
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load categories dynamically from the loaded agents.
   * Only categories that have at least one agent assigned will be shown as tabs.
   * 
   * @param agents - Array of loaded AgentHubItems
   */
  private loadCategories(agents: AgentHubItem[]): void {
    try {
      this.categories = this.agentHubService.getCategoriesFromAgents(agents);
      this.logger.logInfo(
        `[AgentHub] Loaded ${this.categories.length} dynamic categories: [${this.categories.map(c => c.name).join(', ')}]`,
        'loadCategories'
      );
    } catch (error: any) {
      this.logger.logInfo('[AgentHub] Error loading categories', 'loadCategories', error);
      this.categories = [];
    }
  }

  /**
   * Load all agents from service
   * Categories are derived dynamically after agents are loaded
   */
  private loadAgents(): void {
    this.isLoading = true;
    
    this.agentHubService.getAllAgents()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.allAgents = agents;
          this.loadCategories(agents); // Derive categories dynamically from loaded agents
          this.loadDefaultAgents(); // Load default agents after all agents are available
          this.applyFilters();
          this.isLoading = false;
        },
        error: (error) => {
          this.logger.logInfo('[AgentHub] Error loading agents', 'loadAgents', error);
          this.allAgents = [];
          this.filteredAgents = [];
          this.categories = [];
          this.isLoading = false;
        }
      });
  }

  /**
   * Load default agents from localStorage
   */
  private loadDefaultAgents(): void {
    try {
      const stored = localStorage.getItem(this.DEFAULT_AGENTS_KEY);
      if (stored) {
        const ids = JSON.parse(stored) as string[];
        this.defaultAgents = this.allAgents.filter(a => ids.includes(a.id));
        this.logger.logInfo(`[AgentHub] Loaded ${this.defaultAgents.length} default agents`, 'loadDefaultAgents');
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentHub] Error loading default agents', 'loadDefaultAgents', error);
      this.defaultAgents = [];
    }
  }

  /**
   * Save default agents to localStorage
   */
  private saveDefaultAgents(): void {
    try {
      const ids = this.defaultAgents.map(a => a.id);
      localStorage.setItem(this.DEFAULT_AGENTS_KEY, JSON.stringify(ids));
      this.logger.logInfo(`[AgentHub] Saved ${ids.length} default agents`, 'saveDefaultAgents');
    } catch (error: any) {
      this.logger.logInfo('[AgentHub] Error saving default agents', 'saveDefaultAgents', error);
    }
  }

  /**
   * Subscribe to favorites updates
   */
  private subscribeFavorites(): void {
    this.agentHubService.getFavorites()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (favorites) => {
          this.favoriteIds = new Set(favorites);
          // Update isFavorite status on all agents
          this.allAgents = this.allAgents.map(agent => ({
            ...agent,
            isFavorite: this.favoriteIds.has(agent.id)
          }));
          this.applyFilters();
        },
        error: (error) => {
          this.logger.logInfo('[AgentHub] Error subscribing to favorites', 'subscribeFavorites', error);
        }
      });
  }

  /**
   * Handle search query changes
   * 
   * @param query - New search query
   */
  onSearchChanged(query: string): void {
    this.searchQuery = query;
    
    // Use the search API mock
    this.agentHubService.searchAgents(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Get IDs of default agents to exclude from main grid
          const defaultAgentIds = new Set(this.defaultAgents.map(a => a.id));
          
          // Filter out default agents from search results
          let results = response.agents.filter(agent => !defaultAgentIds.has(agent.id));
          
          // Apply category filter on top of search results
          if (this.activeCategory !== 'all') {
            results = results.filter(agent => agent.category.id === this.activeCategory);
          }
          
          this.filteredAgents = results;
        },
        error: (error) => {
          this.logger.logInfo('[AgentHub] Error searching agents', 'onSearchChanged', error);
        }
      });
  }

  /**
   * Handle category filter change
   * 
   * @param categoryId - Selected category ID
   */
  onCategoryChanged(categoryId: string): void {
    this.activeCategory = categoryId;
    this.applyFilters();
  }

  /**
   * Apply current filters (search + category + favorites) to agents
   * Excludes default agents from the main grid to avoid duplication
   */
  private applyFilters(): void {
    // Get IDs of default agents to exclude from main grid
    const defaultAgentIds = new Set(this.defaultAgents.map(a => a.id));
    
    // Start with all agents, excluding default ones
    let filtered = this.allAgents.filter(agent => !defaultAgentIds.has(agent.id));

    // Apply favorite filter first
    if (this.isFavoriteFilterActive) {
      filtered = filtered.filter(agent => this.favoriteIds.has(agent.id));
    }

    // Apply search filter - translate agent properties before comparing
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(agent => {
        const translatedName = this.translate.instant(agent.name).toLowerCase();
        const translatedDescription = this.translate.instant(agent.description).toLowerCase();
        const translatedCategoryName = this.translate.instant(agent.category.name).toLowerCase();
        return translatedName.includes(query) ||
          translatedDescription.includes(query) ||
          translatedCategoryName.includes(query);
      });
    }

    // Apply category filter
    if (this.activeCategory !== 'all') {
      filtered = filtered.filter(agent => agent.category.id === this.activeCategory);
    }

    this.filteredAgents = filtered;
  }

  /**
   * Handle favorite filter toggle
   * 
   * @param isActive - Whether favorite filter should be active
   */
  onFavoriteFilterToggled(isActive: boolean): void {
    this.isFavoriteFilterActive = isActive;
    this.logger.logInfo(`[AgentHub] Favorite filter toggled: ${isActive}`, 'onFavoriteFilterToggled');
    this.applyFilters();
  }

  /**
   * Handle favorite toggle on an agent
   * 
   * @param agentId - ID of the agent to toggle
   */
  onFavoriteToggled(agentId: string): void {
    this.agentHubService.toggleFavorite(agentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.logger.logInfo(
              `[AgentHub] Favorite toggled: ${agentId} -> ${response.isFavorite}`,
              'onFavoriteToggled'
            );
          }
        },
        error: (error) => {
          this.logger.logInfo('[AgentHub] Error toggling favorite', 'onFavoriteToggled', error);
        }
      });
  }

  /**
   * Handle info icon click on an agent
   * Opens the agent info popup with the selected agent's details
   * 
   * @param agentId - ID of the agent
   */
  onInfoClicked(agentId: string): void {
    this.logger.logInfo(`[AgentHub] Info clicked for agent: ${agentId}`, 'onInfoClicked');
    
    // Find the agent in our list
    const agent = this.allAgents.find(a => a.id === agentId);
    if (agent) {
      this.selectedAgentForInfo = agent;
      this.showInfoPopup = true;
    }
  }

  /**
   * Handle info popup close event
   */
  onInfoPopupClosed(): void {
    this.showInfoPopup = false;
    this.selectedAgentForInfo = null;
  }

  /**
   * Handle "Use" button click from info popup
   * Navigates to the selected agent
   * 
   * @param agentId - ID of the agent to use
   */
  onUseAgent(agentId: string): void {
    this.logger.logInfo(`[AgentHub] Use agent from popup: ${agentId}`, 'onUseAgent');
    this.showInfoPopup = false;
    this.selectedAgentForInfo = null;
    this.agentOrchestrator.switchToAgent(agentId);
  }

  /**
   * Handle "Make it default agent" click from info popup
   * Adds the agent to the Default section
   * 
   * @param agentId - ID of the agent to set as default
   */
  onMakeDefaultAgent(agentId: string): void {
    this.logger.logInfo(`[AgentHub] Make default agent: ${agentId}`, 'onMakeDefaultAgent');
    
    // Check if agent is already in default list
    const isAlreadyDefault = this.defaultAgents.some(a => a.id === agentId);
    if (isAlreadyDefault) {
      this.logger.logInfo(`[AgentHub] Agent ${agentId} is already default`, 'onMakeDefaultAgent');
      return;
    }
    
    // Find the agent and add to default list
    const agent = this.allAgents.find(a => a.id === agentId);
    if (agent) {
      this.defaultAgents = [...this.defaultAgents, { ...agent, isDefault: true }];
      this.saveDefaultAgents();
      
      // Update the agent's isDefault flag in allAgents
      this.allAgents = this.allAgents.map(a => ({
        ...a,
        isDefault: a.id === agentId ? true : a.isDefault
      }));
      
      // Reapply filters so the agent disappears from the main grid
      this.applyFilters();
      
      this.logger.logInfo(`[AgentHub] Added agent ${agentId} to defaults. Total defaults: ${this.defaultAgents.length}`, 'onMakeDefaultAgent');
    }
    
    // Close the popup after making default
    this.showInfoPopup = false;
    this.selectedAgentForInfo = null;
  }

  /**
   * Handle unpin (remove from default) click from agent card
   * Removes the agent from the Default section
   * 
   * @param agentId - ID of the agent to remove from default
   */
  onUnpinAgent(agentId: string): void {
    this.logger.logInfo(`[AgentHub] Unpin agent: ${agentId}`, 'onUnpinAgent');
    
    // Remove from default agents list
    this.defaultAgents = this.defaultAgents.filter(a => a.id !== agentId);
    this.saveDefaultAgents();
    
    // Update the agent's isDefault flag in allAgents
    this.allAgents = this.allAgents.map(a => ({
      ...a,
      isDefault: a.id === agentId ? false : a.isDefault
    }));
    
    // Reapply filters so the agent appears back in the main grid
    this.applyFilters();
    
    this.logger.logInfo(`[AgentHub] Removed agent ${agentId} from defaults. Remaining defaults: ${this.defaultAgents.length}`, 'onUnpinAgent');
  }

  /**
   * Handle card click (select agent)
   * Navigates to the agent's landing page by switching to the agent
   * 
   * @param agent - Clicked agent
   */
  onCardClicked(agent: AgentHubItem): void {
    this.logger.logInfo(`[AgentHub] Card clicked: ${agent.id}`, 'onCardClicked');
    
    // Check if streaming is active - show confirmation popup before switching
    if (this.streamingStateService.getCurrentStreamingState()) {
      this.logger.logInfo(`[AgentHub] Streaming active - requesting confirmation before switching to ${agent.id}`, 'onCardClicked');
      this.agentOrchestrator.checkStreamingAndExecute(
        () => this.agentOrchestrator.switchToAgent(agent.id),
        'agent_switch'
      );
      return;
    }
    
    // Switch to the selected agent - this will:
    // 1. Set the current agent ID in the orchestrator
    // 2. Show the agent landing page
    // 3. The chatbot component will detect agent mode and show agent-chat-container
    this.agentOrchestrator.switchToAgent(agent.id);
  }

  /**
   * Check if an agent is favorited
   * 
   * @param agentId - Agent ID to check
   * @returns boolean - True if favorited
   */
  isFavorite(agentId: string): boolean {
    return this.favoriteIds.has(agentId);
  }

  /**
   * Get total count of agents
   * Returns the count of currently filtered agents (respects search, category, and favorite filters)
   * 
   * @returns number - Total agent count
   */
  get totalAgentCount(): number {
    return this.filteredAgents.length;
  }

  /**
   * Track by function for ngFor performance
   * 
   * @param index - Item index
   * @param agent - Agent item
   * @returns string - Unique identifier
   */
  trackByAgent(index: number, agent: AgentHubItem): string {
    return agent.id;
  }

  /**
   * Check if there are no results after filtering
   * 
   * @returns boolean - True if no agents match filters
   */
  get hasNoResults(): boolean {
    return !this.isLoading && this.filteredAgents.length === 0;
  }

  /**
   * Check if there are default agents to display
   * 
   * @returns boolean - True if default agents exist
   */
  get hasDefaultAgents(): boolean {
    return this.defaultAgents.length > 0;
  }
}
